import React, { useMemo } from 'react';
import withInjectedProps from '@atlassian/jira-common-util-with-injected-props/src/index.tsx';
import { JsdGettingStartedPanelShouldRender } from '@atlassian/jira-servicedesk-getting-started-panel/src/async.tsx';
import QueuesApiDataProviderDI from '@atlassian/jira-servicedesk-queues-available-queues/src/services/queues-api-data-provider/main.tsx';
import QueueProviderDI from '@atlassian/jira-servicedesk-queues-available-queues/src/ui/queue-provider/index.tsx';
import { useCategorizedQueues } from '@atlassian/jira-servicedesk-queues-categorized-store/src/main.tsx';
import { ServiceDeskDocumentTitle } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/document-title/index.tsx';
import { usePageContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/page-context/main.tsx';
import { SpaPageWrapper } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/page-wrapper/index.tsx';
import Spinner from '@atlassian/jira-common-components-spinner/src/view.tsx';
import QueuesPushProviderDI from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/queues-push-provider/index.tsx';
import { toItsmPractice } from '@atlassian/jira-servicedesk-work-category/src/main.tsx';
import { toProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { MarkProductStart } from '@atlassian/jira-spa-performance-breakdown/src/utils/mark-product-start/index.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useStylingPatch,
	QueueNav3StylingPatchContainer,
} from '@atlassian/jira-horizontal-nav-queue/src/controllers/styling-patch/index.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { servicedeskQueuesNewRoute } from '@atlassian/jira-router-routes-servicedesk-queues-routes/src/servicedeskQueuesNewRoute.tsx';
import { servicedeskPracticeNewRoute } from '@atlassian/jira-router-routes-servicedesk-queues-routes/src/servicedeskPracticeNewRoute.tsx';
import { useOnFavoritesChanged } from './controllers/favoriting/index.tsx';

import type { Props } from './types.tsx';
import { pageToQueues } from './utils/data-transformer.tsx';

const QueuesAppBase = (props: Props) => {
	const { QueuesPushProvider, pageId } = props;

	const [{ match }] = useRouter();

	const currentRoute = useCurrentRoute();

	const [{ data: pageContextData, error }] = usePageContext();

	const { tenantContext, projectContext, serviceDeskContext } = pageContextData || {};
	const { baseUrl = '' } = tenantContext || {};
	const { projectName } = projectContext || {};

	const pageContextProps = useMemo(
		() => ({ ...tenantContext, ...projectContext, ...serviceDeskContext }),
		[projectContext, serviceDeskContext, tenantContext],
	);

	const projectKey = toProjectKey(match.params.projectKey || '');
	const practice = toItsmPractice(match.params.practiceType);

	const [categorizedQueues, categorizedQueuesLoading, categorizedError, methods] =
		useCategorizedQueues(baseUrl, projectKey, practice);

	let selectedQueueName: string | undefined;

	if (categorizedQueues && match.params.queueId) {
		const selectedQueue = categorizedQueues.find(
			(queue) => queue.id === Number(match.params.queueId),
		);
		selectedQueueName = selectedQueue ? selectedQueue.name : undefined;
	}

	useOnFavoritesChanged(projectKey, practice, categorizedQueues, methods.editItemFavorite);

	const { shouldUseStylingPatch: shouldShowContainer } = useStylingPatch();

	const isLoading = (!pageContextData && !error) || categorizedQueuesLoading;
	const isNewQueuePage =
		currentRoute.name === servicedeskQueuesNewRoute.name ||
		currentRoute.name === servicedeskPracticeNewRoute.name;
	let subSectionName = selectedQueueName;
	if (isNewQueuePage && fg('jsm_queue_a11y_improvement_batch2')) {
		subSectionName = 'New Queue';
	}
	return (
		<>
			<MarkProductStart />
			<SpaPageWrapper
				pageId={pageId}
				baseUrl={baseUrl}
				currentUrl={match.url}
				// @ts-expect-error - TS2322 - Type '{ projectId: number; projectKey: string; projectName: string; projectType: ProjectType; isAdmin: boolean; isProjectSimplified: boolean; } | undefined' is not assignable to type 'ProjectContext | null | undefined'.
				projectContext={projectContext}
				isLoading={
					fg('jsm_views_inside_queues_-_main_flag') ? !shouldShowContainer && isLoading : isLoading
				}
				error={error}
			>
				{isLoading && shouldShowContainer && fg('jsm_views_inside_queues_-_main_flag') && (
					<QueueNav3StylingPatchContainer>
						<Spinner />
					</QueueNav3StylingPatchContainer>
				)}
				<QueuesPushProvider projectKey={projectKey}>
					{({ push, replace: providerReplace }) => (
						<>
							<ServiceDeskDocumentTitle
								// @ts-expect-error - TS2322 - Type '{ projectName: string | undefined; section: string; }' is not assignable to type 'IntrinsicAttributes & Diff<(Diff<RefAttributes<any> | (RefAttributes<any> & { children?: ReactNode; }), Partial<RefAttributes<...> | (RefAttributes<...> & { ...; })>> & RefAttributes<...>) | (Diff<...> & ... 1 more ... & { ...; }), InjectIntlVoidProps> & { ...; }'.
								projectName={projectName}
								section="queues"
								subsection={subSectionName}
							/>

							<>
								{props.children({
									push,
									match,
									replace: providerReplace,
									queuesData: {
										data: pageToQueues(categorizedQueues || []),
										error: categorizedError ? categorizedError.message : undefined,
										loading: categorizedQueuesLoading,
									},
									category: practice,
									// @ts-expect-error - TS2322 - Type '{ portalId?: number | undefined; canSeeNewIssueView?: boolean | undefined; userRole?: string | undefined; serviceDeskId?: number | undefined; projectId?: number | undefined; projectKey?: string | undefined; ... 11 more ...; isSiteAdmin?: boolean | undefined; }' is not assignable to type 'PageProps'.
									pageContext: pageContextProps,
								})}
							</>
						</>
					)}
				</QueuesPushProvider>
			</SpaPageWrapper>
			<JsdGettingStartedPanelShouldRender />
		</>
	);
};

export default withInjectedProps(QueuesAppBase, {
	QueueProvider: QueueProviderDI,
	QueuesApiDataProvider: QueuesApiDataProviderDI,
	QueuesPushProvider: QueuesPushProviderDI,
});
